import React from "react";
import { ImageType } from "@/types/productTypes";
import { ImageIcon, Calendar, Info } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/Components/ui/button";

interface ImageCardProps {
   image: ImageType;
   onSelect?: (image: ImageType) => void;
   onDelete?: (id: number) => void;
   onEdit?: (image: ImageType) => void;
   view?: "grid" | "list";
   selected?: boolean;
}

const ImageCard: React.FC<ImageCardProps> = ({
   image,
   onSelect,
   onDelete,
   onEdit,
   view = "grid",
   selected = false,
}) => {
   const handleSelect = (e: React.MouseEvent) => {
      // Stop propagation if we're clicking a button
      if ((e.target as HTMLElement).closest("button")) {
         return;
      }
      if (onSelect) {
         onSelect(image);
      }
   };

   const createdAt = image.createdAt ? new Date(image.createdAt) : null;

   if (view === "grid") {
      return (
         <div
            className={`border rounded-md overflow-hidden cursor-pointer transition-all ${
               selected
                  ? "ring-2 ring-blue-500 border-blue-500"
                  : "hover:border-blue-300"
            }`}
            onClick={handleSelect}
         >
            <div className="aspect-square bg-gray-100 relative">
               {image.url ? (
                  <img
                     src={image.url}
                     alt={image.title || "Image"}
                     className="w-full h-full object-cover"
                  />
               ) : (
                  <div className="flex items-center justify-center h-full">
                     <ImageIcon className="h-12 w-12 text-gray-300" />
                  </div>
               )}

               {/* Admin actions if provided */}
               {(onDelete || onEdit) && (
                  <div className="absolute top-2 right-2 flex space-x-1">
                     {onEdit && (
                        <Button
                           variant="secondary"
                           size="icon"
                           className="h-7 w-7 bg-white opacity-70 hover:opacity-100"
                           onClick={(e) => {
                              e.stopPropagation();
                              onEdit(image);
                           }}
                        >
                           <Info className="h-3.5 w-3.5" />
                        </Button>
                     )}
                     {onDelete && (
                        <Button
                           variant="destructive"
                           size="icon"
                           className="h-7 w-7 opacity-70 hover:opacity-100"
                           onClick={(e) => {
                              e.stopPropagation();
                              onDelete(image.id);
                           }}
                        >
                           <Info className="h-3.5 w-3.5" />
                        </Button>
                     )}
                  </div>
               )}
            </div>

            <div className="p-2 bg-white">
               <div className="truncate text-sm font-medium">
                  {image.title || "Untitled"}
               </div>
               {createdAt && (
                  <div className="flex items-center text-xs text-gray-500 mt-1">
                     <Calendar className="h-3 w-3 mr-1" />
                     {formatDistanceToNow(createdAt, { addSuffix: true })}
                  </div>
               )}
            </div>
         </div>
      );
   }

   // List view
   return (
      <div
         className={`flex items-center border rounded-md p-2 cursor-pointer ${
            selected
               ? "ring-2 ring-blue-500 border-blue-500"
               : "hover:bg-gray-50"
         }`}
         onClick={handleSelect}
      >
         <div className="h-12 w-12 bg-gray-100 rounded mr-3 flex-shrink-0">
            {image.url ? (
               <img
                  src={image.url}
                  alt={image.title || "Image"}
                  className="h-full w-full object-cover rounded"
               />
            ) : (
               <div className="flex items-center justify-center h-full">
                  <ImageIcon className="h-6 w-6 text-gray-300" />
               </div>
            )}
         </div>

         <div className="flex-1 min-w-0">
            <div className="truncate font-medium">
               {image.title || "Untitled"}
            </div>
            {createdAt && (
               <div className="flex items-center text-xs text-gray-500">
                  <Calendar className="h-3 w-3 mr-1" />
                  {formatDistanceToNow(createdAt, { addSuffix: true })}
               </div>
            )}
         </div>

         {/* Admin actions if provided */}
         {(onDelete || onEdit) && (
            <div className="flex space-x-1 ml-2">
               {onEdit && (
                  <Button
                     variant="ghost"
                     size="icon"
                     className="h-8 w-8"
                     onClick={(e) => {
                        e.stopPropagation();
                        onEdit(image);
                     }}
                  >
                     <Info className="h-4 w-4" />
                  </Button>
               )}
               {onDelete && (
                  <Button
                     variant="ghost"
                     size="icon"
                     className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                     onClick={(e) => {
                        e.stopPropagation();
                        onDelete(image.id);
                     }}
                  >
                     <Info className="h-4 w-4" />
                  </Button>
               )}
            </div>
         )}
      </div>
   );
};

export default ImageCard;
